import React, {useCallback, useEffect, useRef, useState} from 'react'
import {OrderSummary as OrderSummaryComponent, DEFAULT_TABS} from './index'
import {buildLocationOrderSummary} from '../../pages/CatalogSelection/utils/orderSummaryUtils'
import {
  SummaryWrapper,
  SummaryBody,
  SummaryActionsSlot,
  LoadingOverlay,
} from './OrderSummaryContainer.styles'
import {Spinner} from '../../components/loader-spinner'
import {CouponSearch} from '../CouponSearch'

// Shared behind ProposalTool's and PurchaseLink's OrderSummary pages. Both
// build a single-element `locations` array for their "active item" (a
// proposal option or a purchase-link location) and hand it to the reusable
// OrderSummaryComponent — this container holds all the logic that used to be
// duplicated between the two: tab/accordion state, due-today price overrides,
// totals, the coupon footer, and the preview-refresh effects.
//
// Callers stay responsible for anything that differs by source: which Redux
// slice the active item comes from, how it's mutated (`onUpdateItem`), and
// their own products-catalog hook results (passed straight through as props).
export function OrderSummaryContainer({
  activeItem,
  currentId,
  idFallback,
  thryvId,
  selectedCountry,
  selectedProducts,
  handleRemoveProduct,
  isNewTLMCAccessible,
  isNewCustomer,
  isUpgrade = false,
  // Opt-in (PurchaseLink wizard): auto-apply DB-configured new-account coupons.
  applyNewAccountDefaults = false,
  userRoles,
  refreshPaymentPreview,
  onUpdateItem,
  onPreviewLoadingChange,
  // PurchaseLink-only: refresh once when a newly-selected item's account data
  // finishes loading, so its due-today pricing is live as soon as it's shown.
  // ProposalTool has no analog (its options are seeded from the same
  // account-lookup step, not loaded independently per option).
  refreshOnItemsLoaded = false,
  isItemLoaded = false,
  flushEdges = false,
  // Optional controls rendered under the summary card, inside the same
  // gradient/border frame (1:Many's influencer + Generate Link).
  actions,
}) {
  const manualCoupons = activeItem?.coupons ?? []

  const [isPreviewLoading, setIsPreviewLoading] = useState(false)
  // Tracks concurrent in-flight preview calls so the spinner stays on until
  // the last one resolves (both effects can fire close together when items
  // change and auto-coupon sync also triggers a second preview shortly after).
  const pendingPreviewCountRef = useRef(0)
  // Catalog Selection's Continue button can navigate away (to Scheduling)
  // while a preview-payment refresh is still in flight — without this guard
  // its .finally() fires setIsPreviewLoading on an already-unmounted
  // container (React "state update on unmounted component" warning).
  const isMountedRef = useRef(true)
  useEffect(
    () => () => {
      isMountedRef.current = false
    },
    [],
  )

  const startPreviewLoading = () => {
    pendingPreviewCountRef.current++
    if (!isMountedRef.current) return
    setIsPreviewLoading(true)
    onPreviewLoadingChange?.(true)
  }

  const endPreviewLoading = () => {
    pendingPreviewCountRef.current--
    if (pendingPreviewCountRef.current === 0 && isMountedRef.current) {
      setIsPreviewLoading(false)
      onPreviewLoadingChange?.(false)
    }
  }

  // Stable string of coupon codes. Switching the active item (proposal option
  // tab or purchase-link location) also changes this — track the previous id
  // and skip the preview when the effect fires from that switch rather than a
  // real coupon change.
  const couponFingerprint = (activeItem?.coupons ?? [])
    .map(c => c.code ?? c.couponCode)
    .join(',')

  const prevCouponStateRef = useRef(null)
  useEffect(() => {
    const prev = prevCouponStateRef.current
    prevCouponStateRef.current = {id: currentId, couponFingerprint}
    if (prev === null) return // mount — seeded by the caller already
    if (prev.id !== currentId) return // active item switch
    startPreviewLoading()
    refreshPaymentPreview(currentId).finally(endPreviewLoading)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [couponFingerprint])

  // Stable string of product codes + quantities. Same active-item-switch caveat.
  const itemsFingerprint = (activeItem?.items ?? [])
    .map(i => {
      const addonsPart = (i.addons ?? [])
        .map(a => `${a.productCode}:${a.quantity ?? 1}`)
        .join('+')
      const configPart = i.configuration?.proposalAmount
        ? `:${i.configuration.proposalAmount}`
        : ''
      return `${i.productCode}:${i.quantity ?? 1}${configPart}${
        addonsPart ? `|${addonsPart}` : ''
      }`
    })
    .join(',')

  const prevItemsStateRef = useRef(null)
  useEffect(() => {
    const prev = prevItemsStateRef.current
    prevItemsStateRef.current = {id: currentId, itemsFingerprint}
    if (prev === null) return // mount
    if (prev.id !== currentId) return // active item switch
    startPreviewLoading()
    refreshPaymentPreview(currentId).finally(endPreviewLoading)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsFingerprint])

  const hasRefreshedItemRef = useRef(null)
  useEffect(() => {
    if (!refreshOnItemsLoaded) return
    if (!isItemLoaded) return
    if (hasRefreshedItemRef.current === currentId) return
    hasRefreshedItemRef.current = currentId
    if (!activeItem?.items?.length) return
    startPreviewLoading()
    refreshPaymentPreview(currentId).finally(endPreviewLoading)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshOnItemsLoaded, isItemLoaded, currentId])

  const [activeTab, setActiveTab] = useState(DEFAULT_TABS[0]?.id)
  const [softwareOpen, setSoftwareOpen] = useState({0: true})
  const [addonsOpen, setAddonsOpen] = useState({})
  const [oneTimeFeeOpen, setOneTimeFeeOpen] = useState({})
  const [couponsOpen, setCouponsOpen] = useState({})

  const items = activeItem?.items ?? []
  const isMonthlyTab = activeTab === 'monthly'
  // When the backend Recurly preview has run, dueTodayAmount already includes
  // tax. Optimistically assume this is the case when thryvId is present (it
  // will be overridden to false if the preview falls back to client-side math).
  const dueTodayIncludesTax = activeItem?.dueTodayIncludesTax ?? !!thryvId

  // tabTotals.today is passed through explicitly so the reusable component
  // uses this dueTodayAmount-based total directly rather than falling back
  // to its internal coupon subtraction (which would double-count discounts
  // already baked into dueTodayAmount).
  const {
    softwareItems,
    addonItems,
    oneTimeFeeItems,
    itemCount,
    total,
    tabItemCounts,
    tabTotals,
  } = buildLocationOrderSummary(items, {isMonthlyTab, isNewTLMCAccessible})

  const location = {
    locationId: currentId ?? idFallback,
    name: '',
    softwareItems,
    addonItems,
    oneTimeFeeItems,
    itemCount,
    total,
    // undefined (Proposal Tool's activeItem never sets this) is treated as
    // loaded — only an explicit false (PurchaseLink's activeLocation before
    // its subscriptions fetch resolves) triggers the loading state.
    isLoaded: activeItem?.isLoaded,
  }

  const productCodes = selectedProducts.map(i => i?.planCode)
  const autoCouponProductCodes = selectedProducts
    .filter(i => !i?.alreadyIncluded)
    .map(i => i?.planCode)

  const handleCouponsChange = useCallback(
    updatedCoupons => {
      if (currentId == null) return
      onUpdateItem(currentId, {coupons: updatedCoupons})
    },
    [onUpdateItem, currentId],
  )

  return (
    <SummaryWrapper hasActions={Boolean(actions)}>
      {isPreviewLoading && (
        <LoadingOverlay>
          <Spinner />
        </LoadingOverlay>
      )}
      <SummaryBody hasActions={Boolean(actions)}>
        <OrderSummaryComponent
          locations={[location]}
          total={total}
          tabs={DEFAULT_TABS}
          flushEdges={flushEdges}
          country={selectedCountry || 'US'}
          isUpgrade={isUpgrade}
          dueTodayIncludesTax={dueTodayIncludesTax}
          tabItemCounts={tabItemCounts}
          tabTotals={tabTotals}
          singleLocation
          activeTab={activeTab}
          onTabChange={setActiveTab}
          openLocations={{0: true}}
          softwareOpen={softwareOpen}
          addonsOpen={addonsOpen}
          oneTimeFeeOpen={oneTimeFeeOpen}
          onToggleSoftware={i =>
            setSoftwareOpen(prev => ({...prev, [i]: !prev[i]}))
          }
          onToggleAddons={i =>
            setAddonsOpen(prev => ({...prev, [i]: !prev[i]}))
          }
          onToggleOneTimeFee={i =>
            setOneTimeFeeOpen(prev => ({...prev, [i]: !prev[i]}))
          }
          onRemoveSoftwareItem={item =>
            item.productCode && handleRemoveProduct(item.productCode)
          }
          onRemoveAddonItem={item =>
            item.productCode && handleRemoveProduct(item.productCode)
          }
          onRemoveOneTimeFeeItem={item =>
            item.productCode && handleRemoveProduct(item.productCode)
          }
          coupons={manualCoupons}
          couponsOpen={couponsOpen}
          onToggleCoupons={i =>
            setCouponsOpen(prev => ({...prev, [i]: !prev[i]}))
          }
          onRemoveCoupon={coupon => {
            if (currentId == null) return
            const targetCode = coupon.code ?? coupon.couponCode
            onUpdateItem(currentId, {
              coupons: manualCoupons.filter(
                c => (c.code ?? c.couponCode) !== targetCode,
              ),
            })
          }}
          footer={
            <CouponSearch
              key={currentId}
              country={selectedCountry || 'US'}
              productCodes={productCodes}
              autoCouponProductCodes={autoCouponProductCodes}
              thryvId={thryvId}
              isNewCustomer={isNewCustomer}
              applyNewAccountDefaults={applyNewAccountDefaults}
              isUpgradeJourney={isUpgrade}
              userRoles={userRoles}
              initialCoupons={activeItem?.coupons ?? []}
              onCouponsChange={handleCouponsChange}
            />
          }
        />
      </SummaryBody>
      {actions && <SummaryActionsSlot>{actions}</SummaryActionsSlot>}
    </SummaryWrapper>
  )
}
